import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { uploadFile, type FamilyFile } from "@/api/family.api";

interface UploadItem {
  uid: string;
  file: File;
  status: "uploading" | "done" | "error";
  result?: FamilyFile;
  errorMsg?: string;
}

interface FileUploadProps {
  /** single yoki multiple fayl yuklash */
  mode?: "single" | "multiple";
  /** Yuklangan fayllar o'zgarganda chaqiriladi */
  onChange: (files: FamilyFile[]) => void;
  accept?: string;
}

function uid() {
  return Math.random().toString(36).slice(2);
}

function getExt(name: string): string {
  return name.includes(".") ? name.split(".").pop()! : "";
}

export function FileUpload({ mode = "multiple", onChange, accept }: FileUploadProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<UploadItem[]>([]);

  const updateItems = (next: UploadItem[]) => {
    setItems(next);
    onChange(next.filter((i) => i.status === "done" && i.result).map((i) => i.result!));
  };

  const handleFiles = async (files: FileList) => {
    const toAdd = mode === "single" ? [files[0]] : Array.from(files);
    const newItems: UploadItem[] = toAdd.map((f) => ({
      uid: uid(),
      file: f,
      status: "uploading",
    }));

    const next = mode === "single" ? newItems : [...items, ...newItems];
    setItems(next);

    const uploaded = await Promise.all(
      newItems.map(async (item) => {
        try {
          const res = await uploadFile(item.file);
          return {
            ...item,
            status: "done" as const,
            result: {
              id: res.fileId,
              fileName: res.fileName,
              fileExtension: getExt(res.fileName),
            },
          };
        } catch {
          return { ...item, status: "error" as const, errorMsg: t("uploadError") };
        }
      })
    );

    const final =
      mode === "single"
        ? uploaded
        : next.map((it) => uploaded.find((u) => u.uid === it.uid) ?? it);
    updateItems(final);
  };

  const removeItem = (id: string) => {
    updateItems(items.filter((i) => i.uid !== id));
  };

  const triggerPicker = () => inputRef.current?.click();

  return (
    <div className="space-y-2">
      {/* File list */}
      {items.map((item) => (
        <div
          key={item.uid}
          className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl border"
          style={{
            background: "var(--c-elevated-1)",
            borderColor:
              item.status === "error"
                ? "rgba(239,68,68,0.4)"
                : item.status === "uploading"
                  ? "var(--c-brand-tint-strong)"
                  : "var(--c-divider)",
          }}
        >
          {item.status === "uploading" ? (
            <svg
              className="w-4 h-4 shrink-0 animate-spin"
              style={{ color: "var(--primary)" }}
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : item.status === "error" ? (
            <svg className="w-4 h-4 shrink-0 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12A9 9 0 113 12a9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 shrink-0" style={{ color: "var(--primary)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}

          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium" style={{ color: item.status === "error" ? "#f87171" : "var(--c-text)" }}>
              {item.file.name}
            </p>
            {item.status === "uploading" && (
              <p className="text-[10px]" style={{ color: "var(--c-text-muted)" }}>{t("uploadUploading")}</p>
            )}
            {item.status === "error" && (
              <p className="text-[10px] text-red-400">{item.errorMsg}</p>
            )}
          </div>

          <button
            type="button"
            onClick={() => removeItem(item.uid)}
            className="shrink-0 p-1 rounded-lg transition-colors active:scale-90"
            style={{ background: "transparent" }}
            aria-label={t("uploadRemove")}
          >
            <svg className="w-3.5 h-3.5" style={{ color: "var(--c-text-muted)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}

      {/* Add button (hidden for single if already has 1) */}
      {(mode === "multiple" || items.length === 0) && (
        <button
          type="button"
          onClick={triggerPicker}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed py-3 text-sm font-medium transition-all active:scale-[0.98]"
          style={{
            borderColor: "var(--c-brand-tint-strong)",
            color: "var(--primary)",
            background: "var(--c-brand-tint)",
          }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t("uploadAdd")}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple={mode === "multiple"}
        accept={accept}
        className="hidden"
        onChange={(e) => e.target.files && handleFiles(e.target.files)}
      />
    </div>
  );
}
